import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { LuCheck, LuChevronsUpDown, LuPlus } from 'react-icons/lu';

import { cn } from 'utils/cn';

import { Button } from './button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from './command';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from './form';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface ComboboxOption {
    value: string;
    label: string;
}

interface ComboboxProps {
    options: ComboboxOption[];
    selectOptionLabel?: string;
    noOptionsLabel?: string;
    value?: string;
    onValueChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    popoverContentClassName?: string;
    disabled?: boolean;
    customFooter?: React.ReactNode;
    onCustomAction?: () => void;
    customActionLabel?: string;
}

interface FormComboboxProps {
    formMethods: UseFormReturn<any, any, any>;
    name: string;
    label?: string;
    placeholder?: string;
    description?: string;
    defaultValue?: string;
    options: ComboboxOption[];
    selectOptionLabel?: string;
    noOptionsLabel?: string;
    className?: string;
    popoverContentClassName?: string;
    required?: boolean;
    disabled?: boolean;
    customFooter?: React.ReactNode;
    onCustomAction?: () => void;
    customActionLabel?: string;
    onChange?: (value: string) => void;
}

export const Combobox = (props: ComboboxProps) => {
    const [open, setOpen] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState('');

    // Use controlled value if provided, otherwise use internal state
    const value = props.value !== undefined ? props.value : internalValue;
    const setValue = props.onValueChange || setInternalValue;

    const noOptionsLabel = props.noOptionsLabel || 'No option found';
    const selectOptionLabel = props.selectOptionLabel || 'Select an option';
    const placeholder = props.placeholder || selectOptionLabel;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn('w-full justify-between', props.className)}
                >
                    <span className="truncate">
                        {value ? props.options.find((option) => option.value === value)?.label : placeholder}
                    </span>
                    <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn('w-full p-0', props.popoverContentClassName)} align="start">
                <Command>
                    <CommandInput
                        placeholder={`Search ${placeholder.toLowerCase()}...`}
                        className="h-9"
                        disabled={props?.disabled}
                    />
                    <CommandList>
                        <CommandEmpty>{noOptionsLabel}</CommandEmpty>
                        {props.options.map((option: ComboboxOption) => (
                            <CommandItem
                                key={option.value}
                                value={option.label}
                                onSelect={(currentValue) => {
                                    const selectedOption = props.options.find(
                                        (opt) => opt.label.toLowerCase() === currentValue.toLowerCase(),
                                    );
                                    if (selectedOption) {
                                        // For FormCombobox, pass the full object; for regular usage, pass the string value
                                        const newValue = selectedOption.value === value ? '' : selectedOption.value;
                                        setValue(newValue);
                                    }
                                    setOpen(false);
                                }}
                            >
                                <LuCheck
                                    className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                                />
                                {option.label}
                            </CommandItem>
                        ))}
                    </CommandList>
                    {props.customFooter ? (
                        props.customFooter
                    ) : props.onCustomAction && props.customActionLabel ? (
                        <div className="border-t p-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start"
                                onClick={(e) => {
                                    e.preventDefault();
                                    props.onCustomAction?.();
                                    setOpen(false);
                                }}
                            >
                                <LuPlus className="mr-2 h-4 w-4" />
                                {props.customActionLabel}
                            </Button>
                        </div>
                    ) : null}
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export const FormCombobox = (props: FormComboboxProps) => {
    return (
        <FormField
            control={props.formMethods.control}
            name={props.name}
            render={({ field }) => {
                // Safely extract current value
                let currentValue = '';
                try {
                    if (field.value && typeof field.value === 'object' && field.value.value) {
                        currentValue = field.value.value;
                    } else if (typeof field.value === 'string') {
                        currentValue = field.value;
                    }
                } catch (error) {
                    currentValue = '';
                }

                const handleValueChange = (stringValue: string) => {
                    try {
                        if (stringValue) {
                            const selectedOption = props.options.find((option) => option.value === stringValue);
                            if (selectedOption) {
                                field.onChange(selectedOption);
                            }
                        } else {
                            field.onChange(undefined);
                        }
                        props.onChange?.(stringValue);
                    } catch (error) {
                        // Handle error silently
                    }
                };

                return (
                    <FormItem className="flex flex-col gap-2">
                        {props.label && <FormLabel>{props.label}</FormLabel>}
                        <FormControl>
                            <div className="relative">
                                <Combobox
                                    options={props.options}
                                    value={currentValue}
                                    onValueChange={handleValueChange}
                                    placeholder={props.placeholder}
                                    selectOptionLabel={props.selectOptionLabel}
                                    noOptionsLabel={props.noOptionsLabel}
                                    className={props.className}
                                    popoverContentClassName={props.popoverContentClassName}
                                    disabled={props?.disabled}
                                    customFooter={props.customFooter}
                                    onCustomAction={props.onCustomAction}
                                    customActionLabel={props.customActionLabel}
                                />
                                {/* Hidden input for native HTML validation */}
                                {props.required && (
                                    <input
                                        type="text"
                                        value={currentValue}
                                        required={props.required}
                                        onChange={() => {}}
                                        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                        </FormControl>
                        {props.description && <FormDescription>{props.description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};

FormCombobox.displayName = 'FormCombobox';
